<div class="min-h-full bg-[#fafaf7] font-sans">

  <!-- LOADING -->
  @if (isLoading()) {
    <div class="flex items-center justify-center min-h-full py-24">
      <div class="flex flex-col items-center gap-5">
        <div class="relative w-12 h-12">
          <div class="absolute inset-0 rounded-full border-4 border-stone-200"></div>
          <div class="absolute inset-0 rounded-full border-4 border-t-primary-400 animate-spin"></div>
        </div>
        <p class="text-stone-600 font-medium text-sm">Chargement&hellip;</p>
      </div>
    </div>
  }

  <!-- FORM -->
  @if (!isLoading()) {
    <div class="animate-fade-in">
      <div class="max-w-[760px] mx-auto px-5 sm:px-8 py-8">

        <!-- Header -->
        <div class="fiche-titlebar grid grid-cols-3 items-center mb-8">
          <div class="justify-self-start">
            <button
              type="button"
              (click)="cancel()"
              class="flex items-center gap-2 text-[#BDBDBD] hover:text-primary-600 transition-colors cursor-pointer group"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span class="text-[11px] font-bold uppercase tracking-wider">Retour aux demandes</span>
            </button>
          </div>
          <div class="justify-self-center">
            <h1 class="page-title">Demande de transfert</h1>
          </div>
          <div class="justify-self-end"></div>
        </div>

        <!-- Card -->
        <div class="bg-white rounded-2xl border border-stone-100 shadow-sm">

          <!-- Card header -->
          <div class="px-6 py-4 border-b border-stone-100 flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
              <svg class="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div>
              <p class="text-sm font-semibold text-stone-800">Nouvelle demande de transfert</p>
              <p class="text-xs text-stone-500">Remplissez le formulaire ci-dessous pour soumettre une demande</p>
            </div>
          </div>

          <!-- Form body -->
          <div class="px-6 py-6 space-y-6">

            <!-- Player search + select -->
            <div class="space-y-2">
              <label class="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Joueur <span class="text-red-500">*</span>
              </label>

              <!-- Search input -->
              <div class="relative">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  [ngModel]="playerSearch()"
                  (ngModelChange)="playerSearch.set($event)"
                  name="player_search"
                  placeholder="Rechercher un joueur par nom ou numéro…"
                  class="w-full pl-9 pr-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                />
              </div>

              <!-- Player select -->
              @if (players().length === 0) {
                <div class="px-3 py-2.5 text-sm text-stone-400 italic bg-stone-50 border border-stone-200 rounded-lg">
                  Aucun joueur disponible
                </div>
              } @else {
                <select
                  [(ngModel)]="form.player_id"
                  name="player_id"
                  class="w-full px-3 py-2.5 text-sm bg-white border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition cursor-pointer"
                >
                  <option [ngValue]="null" disabled>-- S&eacute;lectionner un joueur --</option>
                  @for (player of filteredPlayers; track player.player_id) {
                    <option [ngValue]="player.player_id">
                      {{ player.player_last_name }} {{ player.player_first_name }}
                      @if (player.player_identity_number) {
                        &nbsp;&mdash;&nbsp;{{ player.player_identity_number }}
                      }
                    </option>
                  }
                </select>
                @if (playerSearch() && filteredPlayers.length === 0) {
                  <p class="text-xs text-stone-400 italic">Aucun joueur ne correspond &agrave; la recherche.</p>
                }
              }
            </div>

            <!-- Transfer type + Licence period (2-col grid) -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">

              <!-- Transfer type -->
              <div class="space-y-2">
                <label class="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  Type de transfert <span class="text-red-500">*</span>
                </label>
                <select
                  [(ngModel)]="form.demande_type"
                  name="demande_type"
                  class="w-full px-3 py-2.5 text-sm bg-white border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition cursor-pointer"
                >
                  @for (type of transferTypes; track type.value) {
                    <option [value]="type.value">{{ type.label }}</option>
                  }
                </select>
              </div>

              <!-- Licence period -->
              <div class="space-y-2">
                <label class="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                  P&eacute;riode de licence <span class="text-red-500">*</span>
                </label>
                @if (licencePeriods().length === 0) {
                  <div class="px-3 py-2.5 text-sm text-stone-400 italic bg-stone-50 border border-stone-200 rounded-lg">
                    Aucune p&eacute;riode ouverte
                  </div>
                } @else {
                  <select
                    [(ngModel)]="form.licenceperiod_id"
                    name="licenceperiod_id"
                    class="w-full px-3 py-2.5 text-sm bg-white border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition cursor-pointer"
                  >
                    @for (period of licencePeriods(); track period.licenceperiod_id) {
                      <option [ngValue]="period.licenceperiod_id">{{ period.licenceperiod_name }}</option>
                    }
                  </select>
                }
              </div>

            </div>

            <!-- Motif -->
            <div class="space-y-2">
              <label class="block text-xs font-semibold text-stone-600 uppercase tracking-wider">
                Motif <span class="text-stone-400 font-normal normal-case">(optionnel)</span>
              </label>
              <textarea
                [(ngModel)]="form.motif"
                name="motif"
                rows="4"
                placeholder="Pr&eacute;cisez le motif du transfert si n&eacute;cessaire&hellip;"
                class="w-full px-3 py-2.5 text-sm bg-white border border-stone-200 rounded-lg text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition resize-none"
              ></textarea>
            </div>

          </div>

          <!-- Card footer -->
          <div class="px-6 py-4 border-t border-stone-100 flex items-center justify-end gap-3">

            <!-- Cancel -->
            <button
              type="button"
              (click)="cancel()"
              [disabled]="isSubmitting()"
              class="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-lg border border-stone-200 text-stone-600 bg-white hover:bg-stone-50 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Annuler
            </button>

            <!-- Submit -->
            @if (permService.can('licence', 'demandes', 'submit')) {
              <button
                type="button"
                (click)="submit()"
                [disabled]="isSubmitting() || !form.player_id || licencePeriods().length === 0"
                class="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                @if (isSubmitting()) {
                  <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                  Envoi en cours&hellip;
                } @else {
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Soumettre la demande
                }
              </button>
            }

          </div>
        </div>

      </div>
    </div>
  }

</div>
